import * as React from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { selectTriggerSizes, type SelectTriggerSize } from "@/components/ui/select";

export interface SearchableSelectItem {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  group?: string;
}

export interface SearchableSelectProps {
  items: SearchableSelectItem[];
  value?: string | null;
  defaultValue?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  size?: SelectTriggerSize;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  contentClassName?: string;
  align?: "start" | "center" | "end";
  ariaLabel?: string;
}

function matches(item: SearchableSelectItem, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.label.toLowerCase().includes(q) ||
    (item.description || "").toLowerCase().includes(q) ||
    item.value.toLowerCase().includes(q)
  );
}

export function SearchableSelect({
  items,
  value,
  defaultValue = null,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No results found",
  size = "default",
  disabled,
  clearable = false,
  className,
  contentClassName,
  align = "start",
  ariaLabel,
}: SearchableSelectProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState<string | null>(defaultValue);
  const selectedValue = isControlled ? (value ?? null) : internalValue;

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  // Only keyboard moves scroll the list; mouse hover must not.
  const keyboardMoveRef = React.useRef(false);
  const listId = React.useId();

  const filtered = React.useMemo(
    () => items.filter((item) => matches(item, query)),
    [items, query],
  );

  const selected = React.useMemo(
    () => items.find((i) => i.value === selectedValue) || null,
    [items, selectedValue],
  );

  React.useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const idx = filtered.findIndex((i) => i.value === selectedValue);
    setActiveIndex(idx >= 0 ? idx : 0);
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const commit = (next: string | null) => {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const moveActive = (delta: number) => {
    if (filtered.length === 0) return;
    setActiveIndex((prev) => {
      let next = prev;
      for (let step = 0; step < filtered.length; step += 1) {
        next = (next + delta + filtered.length) % filtered.length;
        if (!filtered[next]?.disabled) return next;
      }
      return prev;
    });
  };

  React.useEffect(() => {
    if (!open || !keyboardMoveRef.current) return;
    keyboardMoveRef.current = false;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) keyboardMoveRef.current = true;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveActive(-1);
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(Math.max(filtered.length - 1, 0));
        break;
      case "Enter": {
        event.preventDefault();
        const item = filtered[activeIndex];
        if (item && !item.disabled) commit(item.value);
        break;
      }
      case "Escape":
        event.preventDefault();
        setQuery("");
        setOpen(false);
        triggerRef.current?.focus();
        break;
      default:
        break;
    }
  };

  // Group items while preserving filtered order.
  const groups = React.useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, SearchableSelectItem[]>();
    filtered.forEach((item) => {
      const key = item.group ?? "";
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(item);
    });
    return order.map((key) => ({ group: key, items: map.get(key)! }));
  }, [filtered]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel ?? (selected ? `${placeholder}: ${selected.label}` : placeholder)}
          className={cn(
            "group flex w-full items-center justify-between gap-2 rounded-lg border border-input bg-card text-left font-medium shadow-sm ring-offset-background transition-[box-shadow,border-color,background-color] duration-150",
            "hover:border-ring/40 hover:bg-card",

            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:border-ring/50 data-[state=open]:ring-2 data-[state=open]:ring-ring/20",
            "disabled:cursor-not-allowed disabled:opacity-50",
            selectTriggerSizes[size],
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {selected?.icon && (
              <span className="flex shrink-0 items-center text-muted-foreground">{selected.icon}</span>
            )}
            <span
              className={cn(
                "truncate",
                !selected && "font-normal text-muted-foreground",
              )}
            >
              {selected ? selected.label : placeholder}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {clearable && selected && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear selection"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isControlled) setInternalValue(null);
                  onChange?.(null);
                }}
                className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none group-data-[state=open]:rotate-180" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        onKeyDown={onKeyDown}
        className={cn(
          "w-[var(--radix-popover-trigger-width)] min-w-[12rem] overflow-hidden rounded-xl border-border/70 p-0 shadow-lg",
          "motion-reduce:animate-none",
          contentClassName,
        )}
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            role="combobox"
            aria-autocomplete="list"
            aria-controls={listId}
            aria-expanded={open}
            aria-label={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div ref={listRef} id={listId} role="listbox" className="max-h-72 overflow-y-auto p-1.5">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
          )}
          {groups.map(({ group, items: groupItems }) => (
            <div key={group || "__ungrouped"}>
              {group && (
                <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {group}
                </div>
              )}
              {groupItems.map((item) => {
                const index = filtered.indexOf(item);
                const isSelected = item.value === selectedValue;
                const isActive = index === activeIndex;
                return (
                  <div
                    key={item.value}
                    role="option"
                    data-index={index}
                    aria-selected={isSelected}
                    aria-disabled={item.disabled || undefined}
                    onMouseEnter={() => !item.disabled && setActiveIndex(index)}
                    onClick={() => !item.disabled && commit(item.value)}
                    className={cn(
                      "flex cursor-default items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                      isActive && !item.disabled && "bg-accent text-accent-foreground",
                      item.disabled && "pointer-events-none opacity-50",
                    )}
                  >
                    {item.icon && (
                      <span className="flex shrink-0 items-center text-muted-foreground">{item.icon}</span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className={cn("block truncate", isSelected && "font-medium")}>
                        {item.label}
                      </span>
                      {item.description && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.description}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 shrink-0 text-foreground">
                        <title>Selected</title>
                      </Check>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
